import { dfs } from "@p0security/shared/graph/graph";
import { ConnectedNode, isConnectedNode } from "@p0security/shared/graph/types";
import {
  AssessmentNodes,
  IdentityNode,
} from "@p0security/shared/types/assessment/data/index";
import { TIME_TO_DEBOUNCE, sleep } from "@p0security/shared/util/sleep";
import { List, Segmented, Spin } from "antd";
import { ConditionalVirtualList } from "components/common/ConditionalVirtualList";
import { VerticalSpacedDiv } from "components/divs";
import { useGuardedEffect } from "hooks/useGuardedEffect";
import { Dictionary, mapValues, omit, omitBy, size, sortBy } from "lodash";
import { useMemo, useState } from "react";
import { staticError } from "utils/console";

import { IdentityLink } from "../../cells/Identity";
import { MemberIdentityDisplay } from "./MemberIdentityDisplay";

type AssessmentIdentity = AssessmentNodes["identity"] & {
  id: string;
};

export type MemberOption = "all" | "direct" | "indirect";

const arrayToRecord = (
  nodes: ConnectedNode<AssessmentNodes, keyof AssessmentNodes>[]
) =>
  Object.fromEntries(
    nodes
      .filter(isConnectedNode("identity"))
      .map((n) => [n.key, { ...n.data, id: n.key }])
  );

const renderMemberItem = (identity: AssessmentIdentity) => {
  return (
    <List.Item key={identity.label}>
      <IdentityLink data={identity} type="short" show="grant" />
      &nbsp;
      <MemberIdentityDisplay data-testid={identity.label} identity={identity} />
    </List.Item>
  );
};

export const GroupMembers: React.FC<{ node: IdentityNode }> = ({ node }) => {
  const [membershipType, setMembershipType] = useState<MemberOption>("all");
  const [result, setResult] =
    useState<Record<MemberOption, AssessmentIdentity[]>>();

  useGuardedEffect(
    (cancellation) => async () => {
      setResult(undefined);
      await sleep(TIME_TO_DEBOUNCE);
      if (cancellation.isCancelled) return;
      const direct = arrayToRecord(node.parents);
      const before = performance.now();
      const traversed = await dfs<
        AssessmentNodes,
        Record<string, AssessmentIdentity>
      >({ nodes: [node] }, "parents", {
        init: (node) => ({
          [node.key]: { ...node.data, id: node.key } as AssessmentIdentity,
        }),
        // Not interested in lateral movement etc.
        predicate: isConnectedNode("identity"),
        combine: (left, right) => {
          // Mutate in-place to make combination O(N) instead of O(N^2)
          for (const [k, v] of Object.entries(right)) {
            left[k] = v;
          }
          return left;
        },
      });
      // eslint-disable-next-line no-console
      console.log(
        "Time to gather group members",
        (performance.now() - before).toFixed(1),
        "ms"
      );
      if (cancellation.isCancelled) return;
      // Don't include self as member
      const all: Record<string, AssessmentIdentity> = omit(
        traversed[`identity:${node.key}`],
        node.key
      );
      const indirect = omitBy(all, (_, key) => key in direct);
      const localResult = mapValues(
        { all, direct, indirect },
        (r: Dictionary<AssessmentIdentity>) =>
          sortBy(Object.values(r), (i) => i.label)
      );
      setResult(localResult);
    },
    [node],
    staticError
  );

  const options = useMemo(
    () => [
      {
        label: `All (${size(result?.all) ?? 0})`,
        value: "all",
      },
      {
        label: `Direct (${size(result?.direct) ?? 0})`,
        value: "direct",
      },
      {
        label: `Indirect (${size(result?.indirect) ?? 0})`,
        value: "indirect",
      },
    ],
    [result]
  );

  return result === undefined ? (
    <Spin style={{ height: "430px" }} />
  ) : (
    <VerticalSpacedDiv data-testid={"group-members-loaded"}>
      <div style={{ display: "flex", gap: "1em" }}>
        <Segmented
          options={options}
          value={membershipType}
          onChange={setMembershipType as any}
        />
      </div>
      <ConditionalVirtualList<AssessmentIdentity>
        data={result[membershipType]}
        renderItem={renderMemberItem}
        itemKey="label"
      />
    </VerticalSpacedDiv>
  );
};
